import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Network, Shield, Server, Terminal, Cpu, Gauge, Mail, Phone, MapPin, AlertTriangle, Wrench, Info } from 'lucide-react';
import { Button } from '../components/Button';

export function NetworkPage() {
  const { t } = useTranslation();

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">{t('network.title')}</h1>
          <p className="text-3xl mb-4">{t('network.hero.title')}</p>
          <p className="text-xl text-blue-100">{t('network.hero.subtitle')}</p>
          <p className="text-lg text-blue-200 mt-6 max-w-2xl mx-auto">{t('network.hero.description')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Why Us Section */}
        <div className="mb-20 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('network.whyUs.title')}</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">{t('network.whyUs.description')}</p>
        </div>

        {/* Services Grid */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold mb-4 text-center">{t('network.services.title')}</h2>
          <p className="text-lg text-gray-600 mb-8 text-center">{t('network.services.description')}</p>
          <div className="grid md:grid-cols-3 gap-8">
            {t('network.services.items', { returnObjects: true }).map((service: any, index: number) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                {index === 0 && <Cpu className="w-12 h-12 text-blue-600 mb-4" />}
                {index === 1 && <Server className="w-12 h-12 text-blue-600 mb-4" />}
                {index === 2 && <Shield className="w-12 h-12 text-blue-600 mb-4" />}
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Network Prefixes */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-20">
          <h2 className="text-3xl font-bold mb-4 text-center">{t('network.prefixes.title')}</h2>
          <p className="text-gray-600 mb-8 text-center">{t('network.prefixes.description')}</p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('network.prefixes.table.location')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('network.prefixes.table.subnet')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('network.prefixes.table.type')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('network.prefixes.table.description')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {t('network.prefixes.networks', { returnObjects: true }).map((network: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {network.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {network.subnet}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {network.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {network.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Looking Glass */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-20">
          <h2 className="text-3xl font-bold mb-4 text-center">{t('network.lookingGlass.title')}</h2>
          <p className="text-gray-600 text-center mb-8">{t('network.lookingGlass.description')}</p>
          <div className="grid md:grid-cols-3 gap-8">
            {t('network.lookingGlass.tools', { returnObjects: true }).map((tool: any, index: number) => (
              <a 
                key={index} 
                href="https://lg.l2n.it/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-center bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <Terminal className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{tool.name}</h3>
                <p className="text-gray-600">{tool.description}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-3xl font-bold mb-6 text-center">{t('network.contact.title')}</h2>
          <p className="text-gray-600 mb-8 text-center">{t('network.contact.description')}</p>
          
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
              <AlertTriangle className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Abuse</h3>
              <a href="mailto:abuse@synthohosting.net" className="text-blue-600 hover:text-blue-800">
                abuse@synthohosting.net
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
              <Wrench className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Tech</h3>
              <a href="mailto:tech@synthohosting.net" className="text-blue-600 hover:text-blue-800">
                tech@synthohosting.net
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
              <Info className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Info</h3>
              <a href="mailto:info@synthohosting.net" className="text-blue-600 hover:text-blue-800">
                info@synthohosting.net
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
              <Phone className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Phone</h3>
              <a href="tel:+390984180706" className="text-blue-600 hover:text-blue-800">
                +39 0984 180 0706
              </a>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-4">{t('network.contact.company.name')}</h3>
            <div className="space-y-2 text-gray-600">
              <p>{t('network.contact.company.address')}</p>
              <p>{t('network.contact.company.vat')}</p>
              <p>{t('network.contact.company.rea')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}